<script setup lang="ts">
import { $t } from '@/composables/translations/useTranslate.ts';
import { computed } from 'vue';

export interface CheckboxProps {
  id: string;
  name?: string;
  label?: string;
  modelValue?: boolean;
  disabled?: boolean;
  labelSize?: 'default' | 'small';
  errorMessage?: string;
  hasError?: boolean;
  wrapperClass?: string;
  inputClass?: string;
  labelClass?: string;
  required?: boolean;
  showRequiredLabel?: boolean;
}

const props = withDefaults(defineProps<CheckboxProps>(), {
  name: undefined,
  label: undefined,
  modelValue: false,
  disabled: false,
  labelSize: 'default',
  errorMessage: undefined,
  hasError: false,
  wrapperClass: undefined,
  inputClass: undefined,
  labelClass: undefined,
  required: false,
  showRequiredLabel: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  change: [value: boolean];
}>();

const labelId = computed(() => `${props.id}-label`);

const inputClasses = computed(() => {
  const baseClass = 'custom-checkbox';
  const errorClass = props.hasError || props.errorMessage ? 'error' : '';
  return [baseClass, errorClass, props.inputClass].filter(Boolean).join(' ');
});

const labelClasses = computed(() => {
  const baseClasses = 'pl-2 cursor-pointer text-grey';
  const sizeClass = props.labelSize === 'small' ? 'text-sm' : 'text-body';
  return [baseClasses, sizeClass, props.labelClass].filter(Boolean).join(' ');
});

const wrapperClasses = computed(() => {
  const baseClass = 'flex mb-5';
  return [baseClass, props.wrapperClass].filter(Boolean).join(' ');
});

const showError = computed(() => {
  return props.errorMessage && (props.hasError || !props.modelValue);
});

const handleChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const value = target.checked;
  emit('update:modelValue', value);
  emit('change', value);
};
</script>

<template>
  <div :class="showError ? 'error-wrapper' : ''">
    <span
      v-if="showRequiredLabel === true || (showRequiredLabel === undefined && required)"
      class="flex mb-2 text-body-small"
    >
      {{ $t('ibe.common.checkbox-required') }} *
    </span>
    <div :class="wrapperClasses">
      <input
        :id="id"
        :class="inputClasses"
        type="checkbox"
        :name="name"
        :checked="modelValue"
        :disabled="disabled"
        :aria-labelledby="labelId"
        :aria-checked="modelValue ? 'true' : 'false'"
        :aria-invalid="showError ? 'true' : undefined"
        role="checkbox"
        @change="handleChange"
      />
      <label v-if="label || $slots.label" :id="labelId" :for="id" :class="labelClasses">
        <slot name="label">{{ label }}</slot>
      </label>
    </div>

    <div v-if="showError" class="mt-2">
      <span class="bg-error p-2.5 pr-12 inline-flex gap-2.5 items-center text-white text-sm">
        <i class="w-6 h-5 bg-white icon icon-warning shrink-0"></i>
        {{ errorMessage }}
      </span>
    </div>
  </div>
</template>
